import type { ChangeEvent, FC } from "react";
import { useEffect, useState, type JSX } from "react";

import { useDispatch } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { usePrevious } from "app/components/hooks/usePrevious";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { Footer } from "app/components/SharingModal/components/Footer/Footer";
import { InviteUsersList } from "app/components/SharingModal/components/InviteTeammatesList/InviteUsersList";
import { SharingModalBanner } from "app/components/SharingModal/components/SharingModalBanner/SharingModalBanner";
import { SharingModal } from "app/components/SharingModal/SharingModal";
import type {
    ISharingModalState,
    ISharingModalTextContainer,
    IUserInfoExtended,
} from "app/components/SharingModal/types";
import { ISharingModalOption } from "app/components/SharingModal/types";
import { translate } from "app/filters/i18nFilter";
import { getInitialSharingState } from "app/pages/dashboard/SharedDashboardModal/utils";
import { openDefaultHooksModal } from "app/services/ModalService";
import type { IAccountUser } from "app/sharing/SharingService";
import { allTrackers } from "app/services/track/track";

export const transformAccountUsers = (users: IAccountUser[]): IUserInfoExtended[] => {
    return users.map((user) => ({
        email: user.Email,
        firstName: user.FirstName,
        lastName: user.LastName,
        id: user.Id,
        text: user.User,
    }));
};

const textObject: ISharingModalTextContainer["sharingOptionTextContainer"] = {
    [ISharingModalOption.SHARED]: {
        optionIndex: ISharingModalOption.SHARED,
        get title() {
            return translate("common.default.sharing.modal.shared.title");
        },
        get subtitle() {
            return translate("common.default.sharing.modal.shared.subtitle");
        },
        iconName: "company",
        get notificationDefaultContent() {
            return translate("common.default.sharing.modal.notification");
        },
    },
    [ISharingModalOption.RESTRICTED]: {
        optionIndex: ISharingModalOption.RESTRICTED,
        get title() {
            return translate("common.default.sharing.modal.restricted.title");
        },
        get subtitle() {
            return translate("common.default.sharing.modal.restricted.subtitle");
        },
        iconName: "users",
        get notificationDefaultContent() {
            return translate("common.default.sharing.modal.notification");
        },
    },
};

interface IKeywordsSharingModalProps {
    isOpen: boolean;
    keywordGroup: IUserKeywordGroup;
    users: IUserInfoExtended[];
    onCloseClick: () => void;
    onSubmit: (sharingState: IUserKeywordGroup) => void;
}

export const KeywordsSharingModal: FC<IKeywordsSharingModalProps> = ({
    isOpen,
    keywordGroup,
    users = [],
    onCloseClick,
    onSubmit,
}) => {
    const dispatch = useDispatch();
    const [selectedUsers, setSelectedUsers] = useState<IUserInfoExtended[]>([]);
    const [addedUsers, setAddedUsers] = useState<IUserInfoExtended[]>([]);
    const [removedUsers, setRemovedUsers] = useState<IUserInfoExtended[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sharing, setSharing] = useState<ISharingModalState>({} as ISharingModalState);
    const prevSharing = usePrevious(sharing.sharingState);

    const hasSmartSharing =
        swSettings.components.KeywordAnalysis.resources.KeywordListSmartSharingFeature === EAvailabilityMode.OPEN;

    const headerTextObject = {
        get title() {
            return translate("common.default.sharing.modal.title");
        },
        subtitle: keywordGroup.Name,
    };

    useEffect(() => {
        let selected = [];
        if (keywordGroup.sharedWithUsers?.length > 0) {
            selected = users.filter(({ id }) => {
                return keywordGroup.sharedWithUsers.includes(id.toString());
            });
        }
        setSelectedUsers(selected);
        let sharingState = ISharingModalOption.SHARED;
        if (keywordGroup.sharedWithAccounts?.length === 0 && hasSmartSharing) {
            sharingState = ISharingModalOption.RESTRICTED;
        }
        setSharing(
            getInitialSharingState(
                sharingState,
                selected.map(({ id }) => id),
                {
                    header: headerTextObject,
                    sharingOptionTextContainer: textObject,
                },
                [],
            ),
        );
    }, [keywordGroup.sharedWithUsers, users]);

    useEffect(() => {
        if (sharing.sharingState !== prevSharing) {
            allTrackers.dispatchTrackEvent("share.keyword.group", "change", "share.keyword.group");
        }
    }, [sharing.sharingState, prevSharing]);

    useEffect(() => {
        allTrackers.dispatchTrackEvent(
            "Pop Up",
            isOpen ? "open" : "close",
            isOpen ? "Share keyword group" : "Share keywrod group",
        );
    }, [isOpen]);

    const handleCloseModal = (callParentCallback = true): void => {
        if (callParentCallback) {
            onCloseClick();
        }
        setAddedUsers([]);
        setSharing({
            ...sharing,
            notification: "",
            sharedWithUsersToShow: [...(sharing.sharedWithUsers ?? []), ...(sharing.sharedWithInvitations ?? [])],
        });
    };

    const handleSharingStateChange = (sharingState: ISharingModalState): void => {
        selectedUsers.forEach((user) => {
            if (!sharingState.sharedWithUsersToShow.includes(user.id)) {
                setRemovedUsers([...removedUsers, user]);
                setSelectedUsers(selectedUsers.filter(({ id }) => id !== user.id));
            }
        });
        setSharing(sharingState);
    };

    const ShowSucceedToast = () => {
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    get text() {
                        return translate("common.default.sharing.modal.success.toast.message");
                    },
                }),
            ),
        );
    };

    const onApply = async () => {
        try {
            setIsLoading(true);
            let accountIds = [swSettings.user.accountId as string];
            const newUsersAdd = addedUsers.map((user) => user.id);

            if (sharing.sharingState === ISharingModalOption.RESTRICTED) {
                accountIds = [];
            } else {
                allTrackers.dispatchTrackEvent("Pop Up", "submit-ok", "Share keyword group/with-account/");
            }

            if (newUsersAdd?.length) {
                allTrackers.dispatchTrackEvent(
                    "Pop Up",
                    "submit-ok",
                    `Share keyword group/with-users/${newUsersAdd.length.toString() || ""}`,
                );
            }

            await keywordGroupsQueryClient.shareGroup(keywordGroup.Id, {
                accountIds,
                newUsersAdd: newUsersAdd.map((user) => user.toString()),
                userids: [
                    ...selectedUsers.map((user) => user.id.toString()),
                    ...newUsersAdd.map((user) => user.toString()),
                ],
                message: newUsersAdd.length ? sharing.notification : undefined,
            });

            onSubmit(keywordGroup);
            handleCloseModal(false);
            ShowSucceedToast();
        } catch (e) {
        } finally {
            setIsLoading(false);
        }
    };

    const onNotificationShare = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const message = event.target.value;
        setSharing({ ...sharing, notification: message });
    };

    const renderFooter = (): JSX.Element => (
        <Footer
            onInvite={onApply}
            onCancel={handleCloseModal}
            onClose={handleCloseModal}
            isLoadingSharing={isLoading}
            hasCancelButton={false}
            isSharingView={users.length > 0}
            shareButtonText={translate("common.default.sharing.modal.share.button.text")}
        />
    );

    const renderInviteUsers = (): JSX.Element => (
        <InviteUsersList
            users={users}
            inviteUsersPlaceholder={translate("common.default.sharing.modal.invite.placeholder")}
            invitedUsers={addedUsers}
            setInvitedUsers={setAddedUsers}
        />
    );

    const renderBanner = (): JSX.Element => (
        <SharingModalBanner
            hasCustomIcon={true}
            hasCustomButton={true}
            onCustomButtonClick={() => openDefaultHooksModal()}
            customButtonText={translate("common.default.sharing.modal.banner.cta.text")}
            title={translate("common.default.sharing.modal.banner.title")}
            subtitle={translate("common.default.sharing.modal.banner.subtitle")}
        />
    );

    return (
        sharing && (
            <SharingModal
                headerProps={headerTextObject}
                modalProps={{
                    customStyles: { content: { padding: 0, width: "auto" } },
                    isOpen,
                    onCloseClick: handleCloseModal,
                }}
                text={textObject}
                footerComponent={renderFooter()}
                inviteUsersComponent={renderInviteUsers()}
                isBannerShown={!hasSmartSharing ? addedUsers.length == 0 : null}
                bannerComponent={renderBanner()}
                users={users}
                isRestrictedOptionDisabled={!hasSmartSharing}
                isSharedOptionDisabled={false}
                isSharingView={addedUsers.length > 0}
                onChangeNotification={onNotificationShare}
                onShare={handleSharingStateChange}
                sharing={sharing}
            />
        )
    );
};
